import castArray from 'lodash/castArray'
import first from 'lodash/first'
import isString from 'lodash/isString'
import join from 'lodash/join'
import { GetServerSideProps } from 'next'
import React, { useRef } from 'react'

import { LatestArticlesAndCollectionsBlock } from '@Components/CMS/Blocks/LatestArticlesAndCollectionsBlock'
import { CMSPage } from '@Components/CMS/Page'
import { TableOfContent, TableOfContentItem } from '@Domains/Article/Features/TableOfContent/TableOfContent'
import { Block, BlockTypeWithGlobal, Page, PageType } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo'
import { getJSONFieldValue } from '@Globals/CMS/FieldHelpers'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { PageHOC } from '@Globals/Page/Client'
import { handleRedirect, pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { SleekButtonScript } from '@Globals/Scripts/SleekButton'
import { getSinglePost } from '@Queries/cms/SinglePost.query'

interface ArticleProps extends PageProps {
  blocksPart1: BlockTypeWithGlobal[]
  blocksPart2: BlockTypeWithGlobal[]
  item: Page
  tableOfContents: TableOfContentItem[]
}

type Params = {
  slug: string | string[]
}

const PLACE_TABLE_CONTENT_ABOVE_TITLE = 2

type ArticleItems = {
  blocksPart1: BlockTypeWithGlobal[]
  blocksPart2: BlockTypeWithGlobal[]
  tableOfContents: TableOfContentItem[]
}

const reduceBlocks = (blocks: BlockTypeWithGlobal[]) => blocks.reduce<ArticleItems>((acc, curr) => {
  if (curr.type === 'titleBlock') {
    const title: string = getJSONFieldValue((curr as Block)?.fields || [], 'mainTitle') as string || ''
    const type: string = getJSONFieldValue((curr as Block)?.fields || [], 'type') as string || ''
    if (type === 'H1' || type === 'H2') {
      const id: string = curr?.id as string || ''
      const index = acc.tableOfContents.length

      const headerIndex = +type.slice(1)
      let bullet = '1'
      if (acc.tableOfContents[index - 1]) {
        const prevBullet = `${acc.tableOfContents[index - 1].bullet}`.split('.')

        if (prevBullet.length >= headerIndex) {
          const currBulletArr = prevBullet.slice(0, headerIndex)
          const lastIndex = currBulletArr.length - 1
          currBulletArr[lastIndex] = String((+currBulletArr[lastIndex]) + 1)
          bullet = currBulletArr.join('.')
        } else if (prevBullet.length < headerIndex) {
          bullet = [...prevBullet, 1].join('.')
        }
      }

      acc.tableOfContents.push(({ id, title, type, bullet }))
    }
  }
  if (acc.tableOfContents.length < PLACE_TABLE_CONTENT_ABOVE_TITLE) {
    acc.blocksPart1.push(curr)
  }
  if (acc.tableOfContents.length >= PLACE_TABLE_CONTENT_ABOVE_TITLE) {
    acc.blocksPart2.push(curr)
  }
  return acc
}, { tableOfContents: [], blocksPart1: [], blocksPart2: [] })

export const getServerSideProps: GetServerSideProps<ArticleProps, Params> = async (context) => {
  const { params } = context
  const slug = castArray(params?.slug || [])
  const id = first(slug)

  if (!slug || !id) {
    return {
      notFound: true,
    }
  }

  const apolloClient = initializeApolloFromContext(context)
  const post = await getSinglePost(apolloClient, PageType.Article, `/articles/${join(params?.slug, '/')}`, undefined)
  const { page } = post.data

  if (!page) {
    return {
      notFound: true,
    }
  }

  if (page?.type === 'redirect_permanent' || page?.type === 'redirect_temporary') {
    return handleRedirect(context, page.url, page.type)
  }

  const props = await pageProps(
    context,
    { type: 'articles', id },
    page,
    ['cart', 'common', 'cms', 'footer', 'header', 'sidebar', 'validation', 'shipping', 'products'],
  )

  const { blocksPart1, blocksPart2, tableOfContents } = reduceBlocks((page as Page)?.blocks || [])

  return {
    props: {
      blocksPart1,
      blocksPart2,
      tableOfContents,
      ...props,
      item: page as Page,
    },
  }
}

const hasLeadMagnet = (comps: any[]) => comps.some(c => c && isString(c?.key) && c.key.startsWith('lead-magnet'))
const Article = PageHOC<ArticleProps>(({ blocksPart1, blocksPart2, item, tableOfContents }) => {
  const componentsPart1 = useBlockComponents(blocksPart1 || [])
  const componentsPart2 = useBlockComponents(blocksPart2 || [])
  const excludeIDs = item.id ? [item.id] : undefined
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  return (
    <CMSPage ref={wrapperRef} withoutBackToTop>
      {componentsPart1}
      <TableOfContent
        magnet={hasLeadMagnet(componentsPart1) || hasLeadMagnet(componentsPart2)}
        tableOfContents={tableOfContents}
        wrapperRef={wrapperRef}
      />
      {componentsPart2}
      <LatestArticlesAndCollectionsBlock excludeArticleIDs={excludeIDs} hideCollections />
      <SleekButtonScript />
    </CMSPage>

  )
})

export default Article
